import { MutationCache, onlineManager, QueryCache, QueryClient } from '@tanstack/react-query'
import { reportProblemForCurrentUser } from '@/lib/problemReports'

/**
 * What the person was doing, from the query key.
 *
 * The key's first element is the collection every time, which is close enough
 * to an action for somebody reading a list of problems: "loading receipts"
 * says where to look. Anything stranger falls back to the key itself.
 */
function contextOf(queryKey: readonly unknown[]): string {
  const first = queryKey[0]
  return typeof first === 'string' ? `loading ${first}` : 'loading data'
}

/**
 * §7.5 asks the code-lookup screen to be useful within three seconds of the
 * decision moment, with no spinners at typical volumes. That's a caching
 * requirement more than a rendering one: budget codes and a fiscal year of
 * transactions are small and change rarely, so they're kept fresh in memory
 * and refetched in the background rather than re-requested on every mount.
 */
export const queryClient = new QueryClient({
  // Every failed read and write passes through here, which is the only place
  // that sees them all. Screens report nothing themselves; they would each
  // have to remember to, and the one that forgot would be the one that broke.
  queryCache: new QueryCache({
    onError: (error, query) => {
      void reportProblemForCurrentUser(error, contextOf(query.queryKey))
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      void reportProblemForCurrentUser(error, 'saving a change')
    },
  }),
  defaultOptions: {
    queries: {
      // This is used from a desktop and a phone, often within a minute of each
      // other, so the second device has to notice what the first one did. A
      // five-minute window with no refetch on focus meant a packet created on
      // the computer was simply absent on the phone until something else
      // happened to invalidate it.
      //
      // The reads this costs are negligible: §9 budgets a fiscal year at well
      // under 200 transactions per code, and a refetch is one small query.
      staleTime: 30 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
})

/**
 * Restart the queries that stalled while the connection was gone.
 *
 * A query that starts while the browser is offline does not run: it sits at
 * `fetchStatus: 'paused'` holding a fetch that never began. That fetch still
 * counts as in flight, so `invalidateQueries` and `refetchQueries` both
 * deduplicate against it and do nothing — verified against the running app on
 * 2026-10-01, where a paused query was still paused ten seconds after the
 * connection returned. Resetting discards the stuck fetch and asks again.
 *
 * Scoped by predicate to the paused ones: resetting every active query would
 * throw away good data and blank the screen on every reconnect.
 */
export function retryStalledQueries(): void {
  void queryClient.resetQueries({
    predicate: (query) => query.state.fetchStatus === 'paused',
  })
}

// A phone moving between wifi and cell does this several times a day, and
// before this the stalled screen stayed stalled until something changed a
// query key — which is why switching fiscal years back and forth appeared to
// "fix" an empty dashboard.
onlineManager.subscribe((online) => {
  if (online) retryStalledQueries()
})

/** Query keys in one place, so no cache invalidation misses a caller. */
export const queryKeys = {
  user: (userId: string) => ['user', userId] as const,
  budgetCodes: (userId: string, fiscalYear: number) =>
    ['budgetCodes', userId, fiscalYear] as const,
  transactions: (userId: string, fiscalYear: number) =>
    ['transactions', userId, fiscalYear] as const,
  transactionsByCode: (userId: string, fiscalYear: number, budgetCodeId: string) =>
    ['transactions', userId, fiscalYear, budgetCodeId] as const,
  // Not scoped by fiscal year: this counts every transaction pointing at a
  // code, which is what decides whether the code can be deleted outright.
  budgetCodeReferences: (userId: string, budgetCodeId: string) =>
    ['budgetCodeReferences', userId, budgetCodeId] as const,
} as const
